import { Panel } from '@components/Common/Panel';
import { Skeleton } from '@mui/material';
import React from 'react';
import './index.scss';

/**
 * State, not decoration. A tile is coloured only when its number means something is right or
 * wrong ("1 open alert"); a count of live jobs is neutral however proud of it we are. Four tile
 * designs used to share the console, two of them giving every tile its own hue (blue, teal,
 * indigo, amber) so colour carried no meaning anywhere.
 */
export type StatTone = 'neutral' | 'success' | 'warning' | 'danger';

export interface StatTileProps {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  /** One muted line under the value: "200 active", "+29 added this week". */
  sublabel?: React.ReactNode;
  /** Route the tile opens. Makes the whole tile a link (see Panel). */
  to?: string;
  /** Accessible name for the link. Defaults to the label. */
  linkLabel?: string;
  tone?: StatTone;
  /** Draws a skeleton in place of the value. */
  loading?: boolean;
  className?: string;
}

/**
 * The one stat tile: optional icon, label, value, optional sublabel, optional link.
 *
 * Dashboard ("Needs attention", "Platform health", the managed book), Referrals, External Jobs
 * and the analytics tabs each drew their own: value-then-UPPERCASE-label in one, label-then-value
 * in another, tinted icon chips in a third. Lay several out in a `cc-stat-grid`.
 */
const StatTile: React.FC<StatTileProps> = ({
  label,
  value,
  icon,
  sublabel,
  to,
  linkLabel,
  tone = 'neutral',
  loading = false,
  className = '',
}) => (
  <Panel
    variant="stat"
    className={`cc-stat-tile cc-stat-tile--${tone} ${className}`.trim()}
    to={to}
    linkLabel={to ? (linkLabel ?? label) : undefined}
  >
    {icon && (
      <span className={`cc-stat-tile__icon cc-stat-tile__icon--${tone}`} aria-hidden="true">
        {icon}
      </span>
    )}
    <span className="cc-stat-tile__body">
      <span className="cc-stat-tile__label">{label}</span>
      {loading ? (
        <Skeleton variant="text" width={72} height={30} />
      ) : (
        <span className="cc-stat-tile__value">{value}</span>
      )}
      {sublabel && !loading && <span className="cc-stat-tile__sub">{sublabel}</span>}
    </span>
  </Panel>
);

export default StatTile;
